/**
 * API-Client. Spricht ausschliesslich mit buchhaltung-api.
 *
 * Kein Token aus dem localStorage wie in crm-web: die Anmeldung liegt in
 * HTTP-only-Cookies auf `.elbklar.com`, `withCredentials` reicht. Aufbau
 * uebernommen aus account-web.
 *
 * Die Aufrufe unten sind **gegen die echten Pfade** geschrieben — die Router
 * aus Nacht 1 (`routers/*.py`) sind der Vertrag: Pfad, Methode, Rechte und
 * Antwortmodell stehen fest, gefuellt werden sie in dieser Nacht von Bahn C
 * und D. Bis dahin antworten sie mit `501`.
 *
 * Deshalb gibt es `VITE_MOCK_API=1`: dann laufen alle Aufrufe gegen
 * `lib/mock.ts` statt gegen das Netz. Der Schalter sitzt an genau einer Stelle
 * (`export const bh` ganz unten), nicht in den Seiten — sonst muesste jede
 * Seite wissen, ob sie gerade echt ist, und man haette zwei Oberflaechen
 * gebaut statt einer.
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import type {
  Ablehnung,
  BankAccountResponse,
  BankListe,
  BankTransactionResponse,
  Belegart,
  Bestaetigung,
  BuchhaltungEinstellungen,
  DocumentListe,
  DocumentPatch,
  DocumentResponse,
  Freigabe,
  IbanBestaetigung,
  IngestHealth,
  LogEintrag,
  ReviewEntscheidung,
  ReviewItemResponse,
  ReviewListe,
  SeedImport,
  SupplierCreate,
  SupplierListe,
  SupplierResponse,
  SupplierUpdate,
  Uebergabeliste,
  UploadErgebnis,
  Zahlung,
  ZuZahlenListe,
  ZuordnungBestaetigen,
} from '@/types';
import { mockApi, type BelegFilter } from '@/lib/mock';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/** Laeuft die Oberflaeche gegen Beispieldaten statt gegen die API? */
export const IST_MOCK = import.meta.env.VITE_MOCK_API === '1';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Abgelaufene Sitzung: zurueck zur Anmeldung, mit dem Ziel im Gepaeck.
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      const ziel = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?ziel=${ziel}`;
    }
    return Promise.reject(error);
  }
);

export async function fetchApi<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.request<T>({ url, ...config });
  return response.data;
}

/**
 * Ein Fehler in Worten, die ein Mensch lesen kann.
 *
 * `501` bekommt einen eigenen Satz: waehrend Nacht 2 ist das der haeufigste
 * Fehler ueberhaupt, und „Request failed with status code 501" hilft dabei
 * niemandem.
 */
export function fehlerText(fehler: unknown): string {
  const antwort = (fehler as { response?: { status?: number; data?: { detail?: string } } })
    ?.response;
  if (antwort?.status === 501) {
    return 'Diesen Teil der API gibt es noch nicht — er wird gerade gebaut.';
  }
  if (antwort?.status === 403) {
    return 'Dafür fehlt die Berechtigung.';
  }
  if (typeof antwort?.data?.detail === 'string') return antwort.data.detail;
  if (fehler instanceof Error) return fehler.message;
  return 'Unbekannter Fehler.';
}

// ---------------------------------------------------------------------------
// Die echten Aufrufe — Pfade aus `routers/*.py`, Praefixe aus `main.py`
// ---------------------------------------------------------------------------

const echteApi = {
  /** `GET /documents` */
  belege(filter: BelegFilter = {}): Promise<DocumentListe> {
    return fetchApi<DocumentListe>('/documents', {
      params: {
        // Der Server nennt den Parameter `status` (Alias), auch wenn die
        // Funktion `status_filter` heisst — `alias="status"` in `Query()`.
        status: filter.status,
        art: filter.art,
        nur_mit_warnungen: filter.nur_mit_warnungen || undefined,
        von: filter.von || undefined,
        bis: filter.bis || undefined,
        suche: filter.suche || undefined,
        offset: filter.offset ?? 0,
        limit: filter.limit ?? 50,
      },
      // Listen gehen als wiederholte Parameter raus (`?status=neu&status=…`),
      // nicht als `status[]` — so liest FastAPI sie.
      paramsSerializer: { indexes: null },
    });
  },

  /** `GET /documents/{id}` */
  beleg(id: string): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}`);
  },

  /**
   * `GET /documents/{id}/file` — als Adresse, nicht als Aufruf.
   *
   * Die Datei landet in einem `<object>` bzw. `<img>`; der Browser holt sie
   * selbst und schickt das Sitzungs-Cookie mit, weil same-origin. Sie durch
   * axios zu ziehen und als Blob-URL zu setzen waere ein Umweg mit
   * Speicherverbrauch und ohne Gewinn.
   */
  belegDateiUrl(id: string): string {
    return `${API_URL}/documents/${id}/file`;
  },

  /** `GET /documents/{id}/log` */
  belegLog(id: string): Promise<LogEintrag[]> {
    return fetchApi<LogEintrag[]>(`/documents/${id}/log`);
  },

  /** `PATCH /documents/{id}` */
  belegKorrigieren(id: string, patch: DocumentPatch): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}`, { method: 'PATCH', data: patch });
  },

  /** `POST /documents/{id}/freigeben` */
  belegFreigeben(id: string, freigabe: Freigabe): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}/freigeben`, {
      method: 'POST',
      data: freigabe,
    });
  },

  /** `POST /documents/{id}/ablehnen` */
  belegAblehnen(id: string, ablehnung: Ablehnung): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}/ablehnen`, {
      method: 'POST',
      data: ablehnung,
    });
  },

  /** `POST /documents/{id}/bezahlt` */
  belegBezahlt(id: string, zahlung: Zahlung): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}/bezahlt`, {
      method: 'POST',
      data: zahlung,
    });
  },

  /** `POST /documents/{id}/auslesen` */
  belegErneutAuslesen(id: string): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}/auslesen`, { method: 'POST' });
  },

  /** `POST /documents/{id}/datev` — `admin`, weil eine Mail nach draussen geht. */
  belegErneutUebergeben(id: string): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}/datev`, { method: 'POST' });
  },

  /** `POST /documents/{id}/ist-beleg?art=…` — `art` ist ein Query-Parameter. */
  belegIstBeleg(id: string, art: Belegart): Promise<DocumentResponse> {
    return fetchApi<DocumentResponse>(`/documents/${id}/ist-beleg`, {
      method: 'POST',
      params: { art },
    });
  },

  /** `POST /documents/{id}/erinnerung-aus` */
  erinnerungAus(id: string): Promise<Bestaetigung> {
    return fetchApi<Bestaetigung>(`/documents/${id}/erinnerung-aus`, { method: 'POST' });
  },

  /**
   * `POST /documents` — Upload.
   *
   * **Mehrere Dateien ergeben EINEN Beleg**: das Feld heisst `dateien` und
   * wird mehrfach angehaengt; der Server fuegt Bilder zu einem PDF zusammen
   * (§15.2). Wer je Bild einen eigenen Aufruf machte, bekaeme drei Belege.
   */
  hochladen(
    dateien: File[],
    optionen: { art?: Belegart; notiz?: string } = {},
    beiFortschritt?: (prozent: number) => void,
  ): Promise<UploadErgebnis> {
    const daten = new FormData();
    dateien.forEach((datei) => daten.append('dateien', datei, datei.name));

    return fetchApi<UploadErgebnis>('/documents', {
      method: 'POST',
      data: daten,
      params: { art: optionen.art, notiz: optionen.notiz },
      // Den JSON-Header der Instanz ueberschreiben: die Grenze des
      // multipart-Bodys setzt der Browser selbst.
      headers: { 'Content-Type': undefined },
      onUploadProgress: (ereignis) => {
        if (!beiFortschritt) return;
        const gesamt = ereignis.total ?? 0;
        beiFortschritt(gesamt ? Math.round((ereignis.loaded / gesamt) * 100) : 0);
      },
    });
  },

  /** `GET /zu-zahlen` */
  zuZahlen(nurFaellig = false): Promise<ZuZahlenListe> {
    return fetchApi<ZuZahlenListe>('/zu-zahlen', { params: { nur_faellig: nurFaellig } });
  },

  /** `GET /bank/accounts` — `admin` */
  bankKonten(): Promise<BankAccountResponse[]> {
    return fetchApi<BankAccountResponse[]>('/bank/accounts');
  },

  /** `GET /bank/transactions` — `admin` */
  bankUmsaetze(nurOffen = true): Promise<BankListe> {
    return fetchApi<BankListe>('/bank/transactions', { params: { nur_offen: nurOffen } });
  },

  /** `POST /bank/transactions/{id}/zuordnen` — `admin` */
  bankZuordnen(id: string, zuordnung: ZuordnungBestaetigen): Promise<BankTransactionResponse> {
    return fetchApi<BankTransactionResponse>(`/bank/transactions/${id}/zuordnen`, {
      method: 'POST',
      data: zuordnung,
    });
  },

  /** `GET /settings` — `admin` */
  einstellungen(): Promise<BuchhaltungEinstellungen> {
    return fetchApi<BuchhaltungEinstellungen>('/settings');
  },

  // -------------------------------------------------------------------------
  // Prüfliste — `GET /review-items`, Bahn G
  // -------------------------------------------------------------------------

  /**
   * `GET /review-items`
   *
   * Der Server gibt nur die eigenen Postfaecher heraus (bzw. alle, wenn Admin).
   * Die Oberflaeche filtert trotzdem noch einmal (`lib/rechte.ts`) — es ist
   * fremde Post, da sind zwei Schloesser angemessen.
   */
  pruefliste(nurOffen = true): Promise<ReviewListe> {
    return fetchApi<ReviewListe>('/review-items', { params: { nur_offen: nurOffen } });
  },

  /** `POST /review-items/{id}/entscheidung` */
  prueflisteEntscheiden(
    id: string,
    entscheidung: ReviewEntscheidung,
  ): Promise<ReviewItemResponse> {
    return fetchApi<ReviewItemResponse>(`/review-items/${id}/entscheidung`, {
      method: 'POST',
      data: entscheidung,
    });
  },

  // -------------------------------------------------------------------------
  // Lieferanten — `admin`, der ganze Router (§15.1 S3)
  // -------------------------------------------------------------------------

  /** `GET /suppliers` */
  lieferanten(suche?: string): Promise<SupplierListe> {
    return fetchApi<SupplierListe>('/suppliers', { params: { suche: suche || undefined } });
  },

  /** `POST /suppliers` */
  lieferantAnlegen(daten: SupplierCreate): Promise<SupplierResponse> {
    return fetchApi<SupplierResponse>('/suppliers', { method: 'POST', data: daten });
  },

  /** `PATCH /suppliers/{id}` */
  lieferantAendern(id: string, daten: SupplierUpdate): Promise<SupplierResponse> {
    return fetchApi<SupplierResponse>(`/suppliers/${id}`, { method: 'PATCH', data: daten });
  },

  /** `POST /suppliers/{id}/ibans` — mit Rueckrufnummer und Gespraechspartner. */
  ibanBestaetigen(id: string, bestaetigung: IbanBestaetigung): Promise<SupplierResponse> {
    return fetchApi<SupplierResponse>(`/suppliers/${id}/ibans`, {
      method: 'POST',
      data: bestaetigung,
    });
  },

  /** `POST /suppliers/import` — Startbestand als JSON. */
  lieferantenSeedImport(daten: SeedImport): Promise<SupplierListe> {
    return fetchApi<SupplierListe>('/suppliers/import', { method: 'POST', data: daten });
  },

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  /** `GET /export/uebergabeliste?zeitraum=JJJJ-MM` */
  uebergabeliste(zeitraum: string): Promise<Uebergabeliste> {
    return fetchApi<Uebergabeliste>('/export/uebergabeliste', { params: { zeitraum } });
  },

  /**
   * `GET /export/dokumente?zeitraum=JJJJ-MM` — als Adresse, nicht als Aufruf.
   *
   * Ein ZIP eines ganzen Monats durch axios zu ziehen und als Blob-URL zu
   * setzen hiesse, den gesamten Belegbestand im Speicher des Browsers zu
   * halten. Der Browser laedt ihn selbst; das Sitzungs-Cookie geht mit, weil
   * same-origin.
   */
  dokumentExportUrl(zeitraum: string): string {
    return `${API_URL}/export/dokumente?zeitraum=${encodeURIComponent(zeitraum)}`;
  },

  /**
   * `GET /health/ingest`
   *
   * Diese Route antwortet **absichtlich mit 503**, wenn der Belegeingang nicht
   * frisch ist — der Koerper ist dabei trotzdem gefuellt (§15.1 S6). Ohne
   * `validateStatus` wuerde axios genau den Fall, den der Status-Punkt
   * anzeigen soll, in einen Fehler verwandeln.
   */
  ingestHealth(): Promise<IngestHealth> {
    return fetchApi<IngestHealth>('/health/ingest', {
      validateStatus: (status) => status === 200 || status === 503,
    });
  },
};

/** Alle Aufrufe der Oberflaeche. Echt — oder gegen Beispieldaten. */
export const bh: typeof echteApi = IST_MOCK
  ? { ...echteApi, ...mockApi }
  : echteApi;

export type { BelegFilter };
export default api;
